import { useQuery } from '@apollo/client'
import { ScrollArea, Stack, Text, Title, Tooltip, useMantineTheme } from '@mantine/core'
import { IconAlertTriangle, IconCheck } from '@tabler/icons-react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { erc20Abi } from 'viem'

import genericTokenDark from '@/assets/icons/generic_token_dark.svg'
import genericTokenLight from '@/assets/icons/generic_token_light.svg'
import metamaskIcon from '@/assets/icons/metamask.svg'
import RootButton from '@/components/Buttons/RootButton'
import Loading from '@/components/Loading'
import TokenDisplay from '@/components/Tokens/TokensDisplay'
import {
  BRIDGE_AVAX_ERC20_REMOTE_ADDRESS,
  BRIDGE_L1_ERC20_REMOTE_ADDRESS,
  LL1_ADDRESS,
  NATIVE_CHAIN_TICKER,
  OLD_RL1_ADDRESS,
  oldRL1Enabled,
  RL1_ADDRESS,
  STAKE_ADDRESS,
  VOTE_ADDRESS,
} from '@/constants/blockchain'
import { useBalancesContext } from '@/contexts/BalancesContext'
import { useChainFunctions } from '@/hooks/useChain'
import { useAuth } from '@/plugins/auth'
import { avalancheWagmiConfig, l1NativeWagmiConfig } from '@/plugins/auth/config'
import { GetUserERC20Tokens, QUERY_GET_USER_ERC20_TOKENS } from '@/schemas/tokens.schema'
import { emptyBalance, processBalance, ProcessedBalance } from '@/utils/utils'

import classesActivity from '../../Activity/Activity.module.css'
import classes from '../../Wallet/BalancesDisplay.module.css'
import ModalSkeleton from '../ModalSkeleton'

const ERROR_DISPLAY_TIME = 5000

type TokenWithBalance = {
  address: string
  name: string
  symbol: string
  decimals: number
  balance: ProcessedBalance
  icon?: string
}

type TokenState = {
  loading: boolean
  added: boolean
  error: string
}

interface TokenRowProps {
  addCall: () => void
  isMetamask: boolean
  token: TokenWithBalance
  isLoading: boolean
  isAdded: boolean
  isError: string
  disableButton: boolean
}

const TokenRow: React.FC<TokenRowProps> = ({
  addCall,
  isMetamask,
  token,
  isLoading,
  isAdded,
  isError,
  disableButton,
}) => {
  const theme = useMantineTheme()
  return (
    <Stack
      style={{
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <TokenDisplay
        token={token.symbol === 'L1' ? NATIVE_CHAIN_TICKER : token.symbol}
        iconDark={!token.icon ? genericTokenDark : undefined}
        iconLight={!token.icon ? genericTokenLight : undefined}
        tokenName={token.name}
        withTooltip
      />
      <Stack
        style={{ gap: '15px', flexDirection: 'row', alignItems: 'center', textAlign: 'right' }}
      >
        <Tooltip
          className={classesActivity.fitContentText}
          label={token.balance.fullValue}
          offset={0}
        >
          <Text className={classes.balanceAmount}>{`${token.balance.display}`}</Text>
        </Tooltip>
        {isMetamask && (
          <RootButton
            secondary
            style1={false}
            onClick={addCall}
            loading={isLoading}
            disabled={isAdded || isError !== '' || disableButton}
          >
            {isAdded && <IconCheck color={theme.colors.l1Primary[0]} style={{ margin: '0 4px' }} />}
            {!isAdded && !isError && (
              <img src={metamaskIcon} alt="Metamask" style={{ width: '20px', height: '20px' }} />
            )}
            {isError && (
              <IconAlertTriangle color={theme.colors.notification[0]} style={{ margin: '0 4px' }} />
            )}
          </RootButton>
        )}
      </Stack>
    </Stack>
  )
}

const ll1andRl1Icon =
  'https://images.ctfassets.net/jeccmtvjaeon/4jQlQnzCwbbUWcLI9SVDiE/8432f68f44d7721399021092d0d36dd4/r_ll1TokenBlack.svg'
const avaxIcon =
  'https://cdnwp-s3.benzinga.com/wp-content/uploads/2021/02/25122900/avalanche-avax-logo.png'
const l1Icon =
  'https://images.ctfassets.net/gcj8jwzm6086/5KPky47nVRvtHKYV0rQy5X/e0d153df56fd1eac204f58ca5bc3e133/L1-YouTube-Avatar.png'

interface ManageTokensModalProps {
  network: string
  close: () => void
}

const ManageTokensModal: React.FC<ManageTokensModalProps> = ({ network, close }) => {
  const { address } = useAuth()
  const theme = useMantineTheme()
  const { t } = useTranslation()
  // Logged In Info
  const isMetamask = localStorage.getItem('Web3Auth-cachedAdapter') === 'metamask'
  const {
    bridge: { hasBridgeAvax, l1NetworkAvaxBalance, avaxNetworkL1Balance },
    ll1: { hasLL1, balance },
    rl1: { hasStaking, balanceNew, balanceOld },
    stake,
    governance: { hasGovernance, vote },
  } = useBalancesContext()
  const { addTokenToMetamask, readContract } = useChainFunctions(l1NativeWagmiConfig)
  const { addTokenToMetamask: addTokenToMetamaskAvax } = useChainFunctions(avalancheWagmiConfig)

  // Known tokens at the top of the list
  const knownTokens: TokenWithBalance[] = useMemo(() => {
    const tokens: TokenWithBalance[] = []
    if (network === 'Lamina1') {
      if (hasBridgeAvax) {
        tokens.push({
          address: BRIDGE_AVAX_ERC20_REMOTE_ADDRESS.toLowerCase(),
          name: 'Avalanche',
          symbol: 'AVAX',
          decimals: 18,
          balance: l1NetworkAvaxBalance,
          icon: avaxIcon,
        })
      }
      if (hasLL1) {
        tokens.push({
          address: LL1_ADDRESS.toLowerCase(),
          name: 'L1 Launch Token',
          symbol: 'LL1',
          decimals: 18,
          balance,
          icon: ll1andRl1Icon,
        })
      }
      if (hasStaking) {
        tokens.push({
          address: RL1_ADDRESS.toLowerCase(),
          name: 'L1 Mining Rewards Token',
          symbol: 'RL1',
          decimals: 18,
          balance: balanceNew,
          icon: ll1andRl1Icon,
        })
        if (oldRL1Enabled) {
          tokens.push({
            address: OLD_RL1_ADDRESS.toLowerCase(),
            name: 'L1 Mining Rewards Token',
            symbol: 'RL1 (Original)',
            decimals: 18,
            balance: balanceOld,
            icon: ll1andRl1Icon,
          })
        }
        tokens.push({
          address: STAKE_ADDRESS.toLowerCase(),
          name: 'Staking Power',
          symbol: 'STAKE',
          decimals: 18,
          balance: stake,
          icon: ll1andRl1Icon,
        })
      }
      if (hasGovernance) {
        tokens.push({
          address: VOTE_ADDRESS.toLowerCase(),
          name: 'Governance',
          symbol: 'VOTE',
          decimals: 18,
          balance: vote,
          icon: ll1andRl1Icon,
        })
      }
    } else {
      tokens.push({
        address: BRIDGE_L1_ERC20_REMOTE_ADDRESS.toLowerCase(),
        name: 'Lamina1',
        symbol: 'L1',
        decimals: 18,
        balance: avaxNetworkL1Balance,
        icon: l1Icon,
      })
    }
    return tokens
  }, [
    hasBridgeAvax,
    hasLL1,
    hasStaking,
    network,
    l1NetworkAvaxBalance,
    avaxNetworkL1Balance,
    balance,
    balanceNew,
    balanceOld,
    stake,
  ])

  // Query indexer for all tokens
  const { data, loading } = useQuery<GetUserERC20Tokens>(QUERY_GET_USER_ERC20_TOKENS, {
    variables: {
      user: address?.toLowerCase(),
      known: knownTokens.map(token => token.address),
    },
    skip: !address || network !== 'Lamina1',
  })

  // Tokens from indexer
  const [tokens, setTokens] = useState<TokenWithBalance[]>([])

  // All tokens
  const allTokens = useMemo(() => knownTokens.concat(tokens), [knownTokens, tokens])

  // State of adding tokens
  const [tokensState, setTokensState] = useState<Record<string, TokenState>>({})

  // Process token balances
  const processTokenBalances = useCallback(
    async (tokenData: GetUserERC20Tokens) => {
      if (!address) return
      const tokensWithBalance: TokenWithBalance[] = await Promise.all(
        tokenData.balance.map(async ({ token }) => {
          let processedBalance = emptyBalance
          try {
            const balanceData = await readContract({
              address: token.collection.id as `0x${string}`,
              abi: erc20Abi,
              functionName: 'balanceOf',
              args: [address],
            })
            processedBalance = processBalance(balanceData as bigint)
          } catch (error) {
            console.error(error)
          }
          return {
            address: token.collection.id,
            name: token.collection.name,
            symbol: token.collection.symbol,
            decimals: token.collection.decimals,
            balance: processedBalance,
          }
        })
      )
      // Sort tokens by balance
      setTokens(tokensWithBalance.sort((a, b) => Number(b.balance.value - a.balance.value)))
    },
    [address]
  )

  // Process tokens from indexer
  useEffect(() => {
    if (data) {
      processTokenBalances(data)
    }
  }, [data])

  const anyLoading = useMemo(
    () => allTokens.some(token => tokensState[token.address]?.loading || false),
    [tokensState]
  )
  const anyError = useMemo(
    () => allTokens.reduce((acc, token) => acc || tokensState[token.address]?.error || '', ''),
    [tokensState]
  )
  const allAdded = useMemo(
    () => allTokens.every(token => tokensState[token.address]?.added || false),
    [tokensState]
  )

  const addToken = useCallback(
    async (token: TokenWithBalance) => {
      // Update loading state for this specific token
      setTokensState(prev => ({
        ...prev,
        [token.address]: { ...prev[token.address], loading: true },
      }))
      try {
        if (network === 'Lamina1') {
          await addTokenToMetamask(token.address, token.icon)
        } else {
          await addTokenToMetamaskAvax(token.address, token.icon)
        }
        // Update success state
        setTokensState(prev => ({
          ...prev,
          [token.address]: { ...prev[token.address], loading: false, added: true },
        }))
      } catch (err: any) {
        // Update error state
        setTokensState(prev => ({
          ...prev,
          [token.address]: { ...prev[token.address], loading: false, error: err.message },
        }))

        // Clear error after timeout
        setTimeout(() => {
          setTokensState(prev => ({
            ...prev,
            [token.address]: { ...prev[token.address], error: '' },
          }))
        }, ERROR_DISPLAY_TIME)
      }
    },
    [allTokens, addTokenToMetamask, addTokenToMetamaskAvax, network]
  )

  const addAllTokens = useCallback(async () => {
    setTokensState(prev => ({ ...prev, all: { ...prev.all, loading: true } }))
    allTokens.map(token =>
      setTokensState(prev => ({
        ...prev,
        [token.address]: { ...prev[token.address], loading: true },
      }))
    )
    // Add tokens one by one
    await allTokens
      .map(async token => addToken(token))
      .reduce((promise, next) => promise.then(() => next), Promise.resolve())
  }, [allTokens, addToken])

  return (
    <ModalSkeleton
      opened
      onClose={close}
      title={t('components.wallet.modal.manageTokens.title', 'Manage Tokens')}
    >
      <Stack style={{ gap: '15px', paddingBottom: '30px' }}>
        <Text>
          {
            t(
              'components.wallet.modal.manageTokens.description',
              'Add supported ERC-20 tokens to your MetaMask wallet to always see your LAMINA1 balances. '
            ) as string
          }
        </Text>
        <Stack style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
          <Title order={4}>{t('components.wallet.modal.manageTokens.subheader', 'Tokens')}</Title>
          {isMetamask && (
            <RootButton
              secondary
              style1={false}
              onClick={() => addAllTokens()}
              loading={tokensState.all?.loading || false}
              disabled={allAdded || anyLoading}
            >
              Add All
            </RootButton>
          )}
        </Stack>
        <ScrollArea h={300}>
          <Stack style={{ gap: '15px' }}>
            {allTokens.map(token => (
              <TokenRow
                key={token.address}
                addCall={() => addToken(token)}
                isMetamask={isMetamask}
                token={token}
                isLoading={tokensState[token.address]?.loading || false}
                isAdded={tokensState[token.address]?.added || false}
                isError={tokensState[token.address]?.error || ''}
                disableButton={anyLoading}
              />
            ))}
          </Stack>
        </ScrollArea>
        {loading && <Loading />}
        {anyError && (
          <Text
            style={{
              color: theme.colors.notification[0],
            }}
          >
            {anyError}
          </Text>
        )}
      </Stack>
    </ModalSkeleton>
  )
}

export default ManageTokensModal
